'use client'

import { useState } from 'react'
import { User } from '@/types'
import { UserFilter } from '@/types/admin'
import { logError, getErrorMessage, createError } from '@/utils/errorHandler'
// import { validateData, userUpdateSchema } from '@/utils/validation' // Unused import
import VerificationBadge from '@/components/landlord/VerificationBadge'
import { getApiUrl } from '@/lib/apiUrl'
import toast from 'react-hot-toast'

interface UserManagementProps {
  users: User[]
  onUpdate: () => void
}

export default function UserManagement({ users, onUpdate }: UserManagementProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [filter, setFilter] = useState<UserFilter['role']>('all')
  const [verificationFilter, setVerificationFilter] = useState<UserFilter['verification']>('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showKycModal, setShowKycModal] = useState(false)

  // Filter users based on selected filters
  const filteredUsers = users.filter(user => {
    const roleMatch = filter === 'all' || user.role === filter || (filter === 'no-role' && !user.role)
    const verificationMatch = verificationFilter === 'all' || 
      (verificationFilter === 'verified' && user.is_verified) ||
      (verificationFilter === 'unverified' && !user.is_verified && !hasRequiredDocuments(user)) ||
      (verificationFilter === 'pending' && !user.is_verified && hasRequiredDocuments(user))
    return roleMatch && verificationMatch
  })

  // Check if user has required documents
  const hasRequiredDocuments = (user: User) => {
    const documents = user.kyc_data?.documents
    return documents?.idDocument && documents?.proofOfAddress
  }

  // Handle user verification
  const handleVerifyUser = async (userId: string, verify: boolean, rejectedReason?: string, isRevoke: boolean = false) => {
    setLoading(userId)
    setMessage('')

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setMessage('Please log in to verify users')
        return
      }

      const apiUrl = getApiUrl()
      
      // Map verify boolean to backend status
      // For revoke, set status back to pending and isVerified to false
      // For reject, set status to rejected
      // For approve, set status to verified
      let status: string
      if (isRevoke) {
        status = 'pending' // Revoke means setting back to pending
      } else if (verify) {
        status = 'verified'
      } else {
        status = 'rejected'
      }
      
      // Call backend API to update KYC status
      const response = await fetch(`${apiUrl}/users/admin/${userId}/kyc`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: status,
          rejectedReason: verify || isRevoke ? undefined : (rejectedReason || 'Verification rejected by admin')
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Failed to update verification: ${response.statusText}`)
      }

      const successMessage = isRevoke 
        ? 'Verification revoked successfully!' 
        : verify 
        ? 'User verified successfully!' 
        : 'User rejected successfully!'
      
      setMessage(successMessage)
      onUpdate()
    } catch (error) {
      const appError = error instanceof Error ? createError(error.message, 'UNKNOWN_ERROR') : createError('An unexpected error occurred', 'UNKNOWN_ERROR')
      logError(appError, 'UserManagement.handleVerifyUser')
      setMessage(getErrorMessage(error))
    } finally {
      setLoading(null)
    }
  }

  const DELETE_CONFIRM_MESSAGE = 'Are you sure you want to delete this user? This action cannot be undone. All their data (properties, applications, payments) will also be deleted.'

  // Handle user deletion — show toast confirmation then perform delete
  const handleDeleteUser = (userId: string) => {
    toast.custom(
      (t) => (
        <div className="bg-white rounded-xl shadow-lg border border-stone-200 p-4 max-w-md">
          <p className="text-stone-700 text-sm mb-4">{DELETE_CONFIRM_MESSAGE}</p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                toast.dismiss(t.id)
                performDeleteUser(userId)
              }}
              className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      ),
      { duration: Infinity }
    )
  }

  const performDeleteUser = async (userId: string) => {
    setLoading(userId)
    setMessage('')

    try {
      // Validate input
      if (!userId || typeof userId !== 'string') {
        setMessage('Invalid user ID provided')
        return
      }

      const token = localStorage.getItem('authToken')
      if (!token) {
        setMessage('Please log in to delete users')
        return
      }

      const apiUrl = getApiUrl()
      
      // Call backend API to delete user
      const response = await fetch(`${apiUrl}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Failed to delete user: ${response.statusText}`)
      }

      toast.success('User deleted successfully!')
      setMessage('User deleted successfully!')
      onUpdate()
    } catch (error) {
      const appError = error instanceof Error ? createError(error.message, 'UNKNOWN_ERROR') : createError('An unexpected error occurred', 'UNKNOWN_ERROR')
      logError(appError, 'UserManagement.performDeleteUser')
      const errMsg = getErrorMessage(error)
      toast.error(errMsg)
      setMessage(errMsg)
    } finally {
      setLoading(null)
    }
  }

  // Get user display name
  const getUserDisplayName = (user: User) => {
    if (user.kyc_data?.firstName && user.kyc_data?.lastName) {
      return `${user.kyc_data.firstName} ${user.kyc_data.lastName}`
    }
    return user.email || 'Unknown User'
  }

  // Get role badge color
  const getRoleBadgeColor = (role: string | null) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800'
      case 'landlord':
        return 'bg-blue-100 text-blue-800'
      case 'client':
        return 'bg-green-100 text-green-800'
      case null:
      case undefined:
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }


  // Handle KYC document viewing
  const handleViewKyc = (user: User) => {
    setSelectedUser(user)
    setShowKycModal(true)
  }

  // Close KYC modal
  const handleCloseKycModal = () => {
    setShowKycModal(false)
    setSelectedUser(null)
  }

  // Get document type display name
  const getDocumentTypeName = (type: string) => {
    if (!type) return 'Unknown Document'
    // Handle both frontend format and backend format
    const normalizedType = type.toLowerCase().replace(/_/g, '').trim()
    switch (normalizedType) {
      case 'iddocument':
      case 'id':
        return 'Government ID (Passport/Driver\'s License)'
      case 'proofofaddress':
      case 'proof_of_address':
        return 'Proof of Address (Utility Bill/Bank Statement)'
      case 'bankstatement':
      case 'bank_statement':
        return 'Bank Statement'
      case 'employmentletter':
      case 'employment_letter':
        return 'Employment Letter'
      case 'incomproof':
      case 'income_proof':
        // Map income_proof to Bank Statement for backward compatibility, but filter it out in display
        return 'Bank Statement'
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
        <div className="text-sm text-gray-500">
          Total: {users.length} users
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Filter</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'landlord' | 'client' | 'admin' | 'no-role')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="landlord">Landlords</option>
              <option value="client">Clients</option>
              <option value="admin">Admins</option>
              <option value="no-role">No Role</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Verification Status</label>
            <select
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value as 'all' | 'pending' | 'verified' | 'unverified')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Review ({users.filter(u => !u.is_verified && hasRequiredDocuments(u)).length})</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-3 rounded-md text-sm ${
          message.includes('error') || message.includes('Failed') 
            ? 'bg-red-100 text-red-700' 
            : 'bg-green-100 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Pending Verification Alert */}
      {users.filter(u => !u.is_verified && hasRequiredDocuments(u)).length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Pending Verifications
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  You have <strong>{users.filter(u => !u.is_verified && hasRequiredDocuments(u)).length} users</strong> with uploaded KYC documents waiting for verification.
                  Click &quot;View KYC&quot; to download and review their documents offline, then &quot;✓ Approve&quot; to verify them.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Verification
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KYC Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {getUserDisplayName(user)}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'No Role'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VerificationBadge user={user} size="sm" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.role === 'admin' ? (
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center space-x-1">
                            <span className="text-blue-600">👑</span>
                            <span>Admin - Verified by default</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            No KYC required
                          </div>
                        </div>
                      ) : user.kyc_data?.documents ? (
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center space-x-1">
                            <span className="text-green-600">✓</span>
                            <span>Documents uploaded</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {Object.keys(user.kyc_data.documents).length} documents
                          </div>
                          {hasRequiredDocuments(user) && !user.is_verified && (
                            <div className="text-xs text-yellow-600 font-medium">
                              Ready for verification
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No documents</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.created_at 
                        ? (() => {
                            try {
                              const date = new Date(user.created_at)
                              return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
                            } catch {
                              return 'N/A'
                            }
                          })()
                        : 'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {user.role === 'admin' ? (
                          <span className="text-blue-600 text-xs px-2 py-1 bg-blue-100 rounded">
                            Admin - No actions needed
                          </span>
                        ) : (
                          <>
                            {user.kyc_data?.documents && (
                              <button
                                onClick={() => handleViewKyc(user)}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                View KYC
                              </button>
                            )}
                            {!user.is_verified && hasRequiredDocuments(user) && (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleVerifyUser(user.id, true)}
                                  disabled={loading === user.id}
                                  className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                                >
                                  {loading === user.id ? 'Verifying...' : '✓ Approve'}
                                </button>
                                <button
                                  onClick={() => {
                                    const reason = prompt('Please provide a reason for rejection:')
                                    if (reason !== null && reason.trim()) {
                                      handleVerifyUser(user.id, false, reason.trim())
                                    }
                                  }}
                                  disabled={loading === user.id}
                                  className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                                >
                                  {loading === user.id ? 'Rejecting...' : '✗ Reject'}
                                </button>
                              </div>
                            )}
                            {user.is_verified && (
                              <button
                                onClick={() => handleVerifyUser(user.id, false, undefined, true)}
                                disabled={loading === user.id}
                                className="px-3 py-1 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 text-sm font-medium"
                              >
                                {loading === user.id ? 'Revoking...' : '✗ Revoke'}
                              </button>
                            )}
                            {!hasRequiredDocuments(user) && !user.is_verified && (
                              <span className="text-gray-400 text-xs px-2 py-1 bg-gray-100 rounded">
                                Needs documents
                              </span>
                            )}
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={loading === user.id}
                              className="text-red-600 hover:text-red-700 disabled:opacity-50"
                            >
                              {loading === user.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No users found matching the current filters.</p>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Users</h3>
          <p className="text-2xl font-bold text-gray-900">{users.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Verified Users</h3>
          <p className="text-2xl font-bold text-green-600">
            {users.filter(u => u.is_verified).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Pending Review</h3>
          <p className="text-2xl font-bold text-yellow-600">
            {users.filter(u => !u.is_verified && hasRequiredDocuments(u)).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Needs Documents</h3>
          <p className="text-2xl font-bold text-red-600">
            {users.filter(u => !u.is_verified && !hasRequiredDocuments(u)).length}
          </p>
        </div>
      </div>

      {/* Verification Guidelines */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Verification Guidelines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Required for Verification:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Government ID (Passport/Driver&apos;s License)</li>
              <li>• Proof of Address (Utility Bill/Bank Statement)</li>
              <li>• Complete personal information</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Verification Process:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Click &quot;View KYC&quot; to access user documents</li>
              <li>• Download all documents for offline review</li>
              <li>• Verify personal information matches documents</li>
              <li>• Check document authenticity and quality</li>
              <li>• Return and click &quot;✓ Approve&quot; to verify user</li>
            </ul>
          </div>
        </div>
      </div>

      {/* KYC Document Viewer Modal */}
      {showKycModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  KYC Documents - {getUserDisplayName(selectedUser)}
                </h3>
                <button
                  onClick={handleCloseKycModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Information */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-gray-900">Name:</span> <span className="text-gray-900 font-medium ml-1">{selectedUser.firstName || selectedUser.kyc_data?.firstName || ''} {selectedUser.lastName || selectedUser.kyc_data?.lastName || ''}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Email:</span> <span className="text-gray-900 font-medium ml-1">{selectedUser.email}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Phone:</span> <span className="text-gray-900 font-medium ml-1">{selectedUser.phone || selectedUser.kyc_data?.phone || (selectedUser.profile as any)?.phone || 'Not provided'}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Date of Birth:</span> <span className="text-gray-900 font-medium ml-1">{
                      selectedUser.profile?.dateOfBirth 
                        ? new Date(selectedUser.profile.dateOfBirth).toLocaleDateString()
                        : selectedUser.kyc_data?.dateOfBirth 
                        ? (selectedUser.kyc_data.dateOfBirth.includes('T') 
                            ? new Date(selectedUser.kyc_data.dateOfBirth).toLocaleDateString()
                            : selectedUser.kyc_data.dateOfBirth)
                        : 'Not provided'
                    }</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-semibold text-gray-900">Address:</span> <span className="text-gray-900 font-medium ml-1">{
                      selectedUser.profile?.address 
                        ? `${selectedUser.profile.address.street || ''}, ${selectedUser.profile.address.city || ''}, ${selectedUser.profile.address.state || ''}`.trim().replace(/^,\s*|,\s*$/g, '') || 'Not provided'
                        : selectedUser.kyc_data?.address || 'Not provided'
                    }</span>
                  </div>
                </div>
              </div>

              {/* KYC Documents */}
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">📋 Review Process</h4>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>1. Download all documents below</p>
                    <p>2. Review them offline for authenticity</p>
                    <p>3. Verify personal info matches documents</p>
                    <p>4. Return here and click &quot;✓ APPROVE VERIFICATION&quot;</p>
                  </div>
                  {(selectedUser.kyc_data?.documents || selectedUser.kyc?.documents) && (
                    <div className="mt-3">
                      <button
                        onClick={async () => {
                          try {
                            // Collect all unique documents (same logic as display)
                            const documentMap = new Map<string, { type: string; url: string; normalizedType: string }>()
                            
                            // Add documents from kyc_data.documents (object format)
                            if (selectedUser.kyc_data?.documents && typeof selectedUser.kyc_data.documents === 'object' && !Array.isArray(selectedUser.kyc_data.documents)) {
                              Object.entries(selectedUser.kyc_data.documents).forEach(([type, url]) => {
                                if (url && typeof url === 'string') {
                                  const normalizedType = type === 'idDocument' ? 'id' : 
                                                       type === 'proofOfAddress' ? 'proof_of_address' :
                                                       type === 'bankStatement' ? 'bank_statement' :
                                                       type === 'employmentLetter' ? 'employment_letter' : type
                                  documentMap.set(normalizedType, { type, url, normalizedType })
                                }
                              })
                            }
                            
                            // Add documents from kyc.documents (array format) if not already present
                            if (selectedUser.kyc?.documents && Array.isArray(selectedUser.kyc.documents)) {
                              selectedUser.kyc.documents.forEach((doc: any) => {
                                if (doc.url && doc.type && doc.type !== 'income_proof') {
                                  const normalizedType = doc.type
                                  if (!documentMap.has(normalizedType)) {
                                    documentMap.set(normalizedType, { type: doc.type, url: doc.url, normalizedType })
                                  }
                                }
                              })
                            }
                            
                            // Filter to only the 4 expected document types
                            const expectedTypes = ['id', 'proof_of_address', 'bank_statement', 'employment_letter']
                            const filteredDocuments = Array.from(documentMap.entries())
                              .filter(([type]) => expectedTypes.includes(type))
                              .map(([type, data]) => ({ ...data, normalizedType: type }))
                            
                            if (filteredDocuments.length === 0) {
                              setMessage('No documents available to download')
                              return
                            }
                            
                            const downloadPromises: Promise<void>[] = []
                            
                            // Helper function to download a single document
                            const downloadDocument = (url: string, filename: string) => {
                              return new Promise<void>((resolve) => {
                                fetch(url)
                                  .then(response => response.blob())
                                  .then(blob => {
                                    const link = document.createElement('a')
                                    const blobUrl = window.URL.createObjectURL(blob)
                                    link.href = blobUrl
                                    link.download = filename
                                    document.body.appendChild(link)
                                    link.click()
                                    document.body.removeChild(link)
                                    window.URL.revokeObjectURL(blobUrl)
                                    // Add delay between downloads
                                    setTimeout(resolve, 500)
                                  })
                                  .catch(() => {
                                    // Fallback to direct link
                              const link = document.createElement('a')
                              link.href = url
                                    link.download = filename
                                    link.target = '_blank'
                              document.body.appendChild(link)
                              link.click()
                              document.body.removeChild(link)
                                    setTimeout(resolve, 500)
                                  })
                              })
                            }
                            
                            // Download each document
                            filteredDocuments.forEach((doc) => {
                              const filename = `${getDocumentTypeName(doc.type || doc.normalizedType).replace(/[^a-z0-9]/gi, '_')}_${selectedUser.kyc_data?.firstName || selectedUser.firstName || 'user'}.pdf`
                              downloadPromises.push(downloadDocument(doc.url, filename))
                            })
                            
                            // Wait for all downloads to complete
                            await Promise.all(downloadPromises)
                            setMessage('All documents downloaded successfully!')
                          } catch (error) {
                            console.error('Error downloading documents:', error)
                            setMessage('Error downloading documents. Please try downloading individually.')
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        📥 Download All Documents
                      </button>
                    </div>
                  )}
                </div>
                
                <h4 className="font-medium text-gray-900">Uploaded Documents</h4>
                {(selectedUser.kyc_data?.documents || selectedUser.kyc?.documents) ? (() => {
                  // Collect all unique documents, prioritizing kyc_data.documents over kyc.documents
                  const documentMap = new Map<string, { type: string; url: string; backendType?: string }>()
                  
                  // First, add documents from kyc_data.documents (object format)
                  if (selectedUser.kyc_data?.documents && typeof selectedUser.kyc_data.documents === 'object' && !Array.isArray(selectedUser.kyc_data.documents)) {
                    Object.entries(selectedUser.kyc_data.documents).forEach(([type, url]) => {
                      if (url && typeof url === 'string') {
                        // Map frontend types to normalized keys
                        const normalizedType = type === 'idDocument' ? 'id' : 
                                             type === 'proofOfAddress' ? 'proof_of_address' :
                                             type === 'bankStatement' ? 'bank_statement' :
                                             type === 'employmentLetter' ? 'employment_letter' : 
                                             type === 'income_proof' ? 'bank_statement' : // Map income_proof to bank_statement for backward compatibility
                                             type.toLowerCase().replace(/([A-Z])/g, '_$1').toLowerCase()
                        // Only add if it's one of the expected types
                        if (['id', 'proof_of_address', 'bank_statement', 'employment_letter'].includes(normalizedType)) {
                          documentMap.set(normalizedType, { type, url })
                        }
                      }
                    })
                  }
                  
                  // Then, add documents from kyc.documents (array format) if not already present
                  if (selectedUser.kyc?.documents && Array.isArray(selectedUser.kyc.documents)) {
                    // First, collect income_proof documents separately
                    const incomeProofDocs: any[] = []
                    const otherDocs: any[] = []
                    
                    selectedUser.kyc.documents.forEach((doc: any) => {
                      if (doc.url && doc.type) {
                        if (doc.type === 'income_proof') {
                          incomeProofDocs.push(doc)
                        } else {
                          otherDocs.push(doc)
                        }
                      }
                    })
                    
                    // Add non-income_proof documents first
                    otherDocs.forEach((doc: any) => {
                      const normalizedType = doc.type
                      if (['id', 'proof_of_address', 'bank_statement', 'employment_letter'].includes(normalizedType) && !documentMap.has(normalizedType)) {
                        documentMap.set(normalizedType, { type: doc.type, url: doc.url, backendType: doc.type })
                      }
                    })
                    
                    // Handle income_proof documents - if we have multiple, use them for both bank_statement and employment_letter
                    if (incomeProofDocs.length > 0) {
                      // Use first income_proof for bank_statement if not already present
                      if (!documentMap.has('bank_statement') && incomeProofDocs[0]) {
                        documentMap.set('bank_statement', { type: 'bankStatement', url: incomeProofDocs[0].url, backendType: 'income_proof' })
                      }
                      // Use second income_proof (or first if only one) for employment_letter if not already present
                      if (!documentMap.has('employment_letter')) {
                        if (incomeProofDocs.length > 1 && incomeProofDocs[1]) {
                          // Use second income_proof for employment_letter
                          documentMap.set('employment_letter', { type: 'employmentLetter', url: incomeProofDocs[1].url, backendType: 'income_proof' })
                        } else if (incomeProofDocs[0] && !documentMap.has('bank_statement')) {
                          // If only one income_proof and bank_statement slot is empty, use it for both
                          documentMap.set('employment_letter', { type: 'employmentLetter', url: incomeProofDocs[0].url, backendType: 'income_proof' })
                        } else if (incomeProofDocs[0] && documentMap.get('bank_statement')?.url === incomeProofDocs[0].url) {
                          // If the same document is used for bank_statement, also use it for employment_letter
                          documentMap.set('employment_letter', { type: 'employmentLetter', url: incomeProofDocs[0].url, backendType: 'income_proof' })
                        }
                      }
                    }
                  }
                  
                  // Filter to only show the 4 expected document types
                  const expectedTypes = ['id', 'proof_of_address', 'bank_statement', 'employment_letter']
                  const filteredDocuments = Array.from(documentMap.entries())
                    .filter(([type]) => expectedTypes.includes(type))
                    .map(([type, data]) => ({ ...data, normalizedType: type }))
                  
                  if (filteredDocuments.length === 0) {
                    return <p className="text-gray-500">No documents uploaded</p>
                  }
                  
                  return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredDocuments.map((doc, index) => (
                        <div key={index} className="border rounded-lg p-4">
                        <h5 className="font-medium text-gray-900 mb-2">
                            {getDocumentTypeName(doc.type || doc.normalizedType)}
                        </h5>
                        <div className="space-y-2">
                            {doc.url ? (
                              <>
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-600 hover:text-blue-700 mr-2"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View
                                </a>
                                <a
                                  href={doc.url}
                            download
                                  className="inline-flex items-center text-green-600 hover:text-green-700"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                                  Download
                                </a>
                              </>
                            ) : (
                              <p className="text-sm text-gray-500">No document URL</p>
                            )}
                            {(doc.normalizedType === 'id' || doc.normalizedType === 'proof_of_address' || 
                              doc.type === 'idDocument' || doc.type === 'proofOfAddress' || 
                              doc.type === 'id' || doc.type === 'proof_of_address') ? (
                            <span className="inline-block px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                Required *
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                              Optional
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  )
                })() : (
                  <p className="text-gray-500">No documents uploaded</p>
                )}
              </div>

              {/* Payment Account Details (for landlords) */}
              {selectedUser.role === 'landlord' && (selectedUser as any).paymentAccount && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-4">Payment Account Details</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p><span className="font-semibold text-gray-900">Account Type:</span> 
                          <span className="text-gray-900 ml-2 capitalize">
                            {(selectedUser as any).paymentAccount.accountType?.replace('_', ' ') || 'Not set'}
                          </span>
                        </p>
                        {(selectedUser as any).paymentAccount.accountType === 'bank' && (
                          <>
                            <p><span className="font-semibold text-gray-900">Bank Name:</span> 
                              <span className="text-gray-900 ml-2">
                                {(selectedUser as any).paymentAccount.bankName || 'N/A'}
                              </span>
                            </p>
                            <p><span className="font-semibold text-gray-900">Account Name:</span> 
                              <span className="text-gray-900 ml-2">
                                {(selectedUser as any).paymentAccount.accountName || 'N/A'}
                              </span>
                            </p>
                            <p><span className="font-semibold text-gray-900">Account Number:</span> 
                              <span className="text-gray-900 ml-2">
                                {(selectedUser as any).paymentAccount.accountNumber ? '••••' + (selectedUser as any).paymentAccount.accountNumber.slice(-4) : 'N/A'}
                              </span>
                            </p>
                            {(selectedUser as any).paymentAccount.routingNumber && (
                              <p><span className="font-semibold text-gray-900">Routing Number:</span> 
                                <span className="text-gray-900 ml-2">
                                  {(selectedUser as any).paymentAccount.routingNumber}
                                </span>
                              </p>
                            )}
                            {(selectedUser as any).paymentAccount.swiftCode && (
                              <p><span className="font-semibold text-gray-900">SWIFT Code:</span> 
                                <span className="text-gray-900 ml-2">
                                  {(selectedUser as any).paymentAccount.swiftCode}
                                </span>
                              </p>
                            )}
                            {(selectedUser as any).paymentAccount.iban && (
                              <p><span className="font-semibold text-gray-900">IBAN:</span> 
                                <span className="text-gray-900 ml-2">
                                  {(selectedUser as any).paymentAccount.iban}
                                </span>
                              </p>
                            )}
                          </>
                        )}
                        {(selectedUser as any).paymentAccount.accountType === 'mobile_money' && (
                          <>
                            <p><span className="font-semibold text-gray-900">Provider:</span> 
                              <span className="text-gray-900 ml-2">
                                {(selectedUser as any).paymentAccount.mobileMoneyProvider || 'N/A'}
                              </span>
                            </p>
                            <p><span className="font-semibold text-gray-900">Mobile Number:</span> 
                              <span className="text-gray-900 ml-2">
                                {(selectedUser as any).paymentAccount.mobileMoneyNumber || 'N/A'}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                      <div>
                        <p><span className="font-semibold text-gray-900">Country:</span> 
                          <span className="text-gray-900 ml-2">
                            {(selectedUser as any).paymentAccount.country || 'N/A'}
                          </span>
                        </p>
                        <p><span className="font-semibold text-gray-900">Currency:</span> 
                          <span className="text-gray-900 ml-2">
                            {(selectedUser as any).paymentAccount.currency || 'NGN'}
                          </span>
                        </p>
                        <p><span className="font-semibold text-gray-900">Verified:</span> 
                          <span className={`ml-2 ${(selectedUser as any).paymentAccount.verified ? 'text-green-600' : 'text-yellow-600'}`}>
                            {(selectedUser as any).paymentAccount.verified ? '✓ Yes' : '✗ No'}
                          </span>
                        </p>
                        {(selectedUser as any).paymentAccount.verifiedAt && (
                          <p><span className="font-semibold text-gray-900">Verified At:</span> 
                            <span className="text-gray-900 ml-2">
                              {new Date((selectedUser as any).paymentAccount.verifiedAt).toLocaleDateString()}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Verification Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><span className="font-semibold text-gray-900">Status:</span> 
                        <span className={`font-semibold ${selectedUser.is_verified ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedUser.is_verified ? ' ✓ Verified' : ' ✗ Not Verified'}
                        </span>
                      </p>
                      <p><span className="font-semibold text-gray-900">Documents:</span> 
                        <span className={`font-semibold ${hasRequiredDocuments(selectedUser) ? 'text-green-600' : 'text-red-600'}`}>
                          {hasRequiredDocuments(selectedUser) ? ' ✓ Complete' : ' ✗ Incomplete'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p><span className="font-semibold text-gray-900">Required Docs:</span> 
                        <span className="text-gray-900">
                          {selectedUser.kyc_data?.documents?.idDocument || selectedUser.kyc?.documents?.some((d: any) => d.type === 'id') ? '✓ ID' : '✗ ID'} / 
                          {selectedUser.kyc_data?.documents?.proofOfAddress || selectedUser.kyc?.documents?.some((d: any) => d.type === 'proof_of_address') ? '✓ Address' : '✗ Address'}
                        </span>
                      </p>
                      <p><span className="font-semibold text-gray-900">Optional Docs:</span> 
                        <span className="text-gray-900">
                          {selectedUser.kyc_data?.documents?.bankStatement || 
                           selectedUser.kyc?.documents?.some((d: any) => d.type === 'bank_statement' || d.type === 'income_proof') ? '✓ Bank' : '✗ Bank'} / 
                          {selectedUser.kyc_data?.documents?.employmentLetter || 
                           selectedUser.kyc?.documents?.some((d: any) => d.type === 'employment_letter' || d.type === 'income_proof') ? '✓ Employment' : '✗ Employment'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <p>Review the documents above before making a decision.</p>
                  </div>
                  <div className="flex space-x-3">
                    {!selectedUser.is_verified && hasRequiredDocuments(selectedUser) && (
                      <button
                        onClick={() => {
                          handleVerifyUser(selectedUser.id, true)
                          handleCloseKycModal()
                        }}
                        disabled={loading === selectedUser.id}
                        className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium text-lg"
                      >
                        {loading === selectedUser.id ? 'Verifying...' : '✓ APPROVE VERIFICATION'}
                      </button>
                    )}
                    {selectedUser.is_verified && (
                      <button
                        onClick={() => {
                          handleVerifyUser(selectedUser.id, false)
                          handleCloseKycModal()
                        }}
                        disabled={loading === selectedUser.id}
                        className="px-6 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 font-medium"
                      >
                        {loading === selectedUser.id ? 'Unverifying...' : '✗ REVOKE VERIFICATION'}
                      </button>
                    )}
                    <button
                      onClick={handleCloseKycModal}
                      className="px-4 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 


